import Button, { ActionButton, BUTTON_VARIANT } from '@bugbug/core/components/Button';
import { Trans, useTranslation } from 'react-i18next';

import type { Project } from '@bugbug/core/types/projects';
import { Header, Footer, ErrorInfo, DeleteIcon } from '~/components/modals/Modal';
import useAppRoutes from '~/hooks/useAppRoutes';
import useModal from '~/hooks/useModal';
import useQueryString from '~/hooks/useQueryString';
import { selectCurrentOrganizationId } from '~/modules/organization/organization.selectors';
import { selectSingleProject } from '~/modules/project/project.selectors';
import { useAppSelector } from '~/modules/store';
import { useClearTestMutation } from '~/modules/tests/tests.api';
import { showTestError } from '~/modules/tests/tests.utils';
import { isInternalServerError } from '~/services/api/utils';
import { ACTIONS } from '~/services/toasts/internalServerError';

import * as S from './DeleteAllGroupsModal.styled';

interface DeleteAllGroupsModalProps {
  testId: string;
  onSuccess?: () => void;
}

const DeleteAllGroupsModal = ({ testId, onSuccess }: DeleteAllGroupsModalProps) => {
  const { t } = useTranslation();
  const modal = useModal();
  const [clearTest, clearTestState] = useClearTestMutation();
  const queryString = useQueryString();
  const appRoute = useAppRoutes('test');
  const organizationId = useAppSelector(selectCurrentOrganizationId);
  const project = useAppSelector(selectSingleProject) as Project;

  const handleDeleteConfirm = async () => {
    try {
      await clearTest({ id: testId }).unwrap();
      onSuccess?.();

      if (queryString.testRunId) {
        appRoute.push('test', {
          testId,
          organizationId,
          projectId: project.id,
          projectSlug: project.slug,
        });
      } else {
        modal.hide();
      }
    } catch (error) {
      showTestError(ACTIONS.UPDATE, error as Error);
    }
  };

  return (
    <S.Container>
      <S.Content>
        <DeleteIcon />
        <div>
          <Header>
            <Trans i18nKey="deleteAllGroupsModal.all.title" defaults="Delete all groups?" />
          </Header>
          <p>
            <Trans
              i18nKey="deleteAllGroupsModal.all.description"
              defaults="Are you sure you want to delete all groups? You <strong>can't undo this.</strong>"
            />
          </p>
        </div>
      </S.Content>
      <Footer>
        <ErrorInfo isVisible={isInternalServerError(clearTestState)} />
        <Button onClick={modal.hide}>{t('deleteAllGroupsModal.cancel', 'Cancel')}</Button>
        <ActionButton
          onClick={handleDeleteConfirm}
          variant={BUTTON_VARIANT.PRIMARY}
          pending={clearTestState.isLoading}
        >
          {t('deleteAllGroupsModal.confirmAll', 'Delete all groups')}
        </ActionButton>
      </Footer>
    </S.Container>
  );
};

export default DeleteAllGroupsModal;
